import * as schema from '../utils/schema';
import {
  INSTRUMENT_TYPE_CURRENCY,
  INSTRUMENT_TYPE_CREDIT_NOTE,
  INSTRUMENT_TYPE_COUPON,
  INSTRUMENT_TYPE_LOYALTY,
  INSTRUMENT_TYPE_REWARD,
  INSTRUMENT_TYPE_CASINO,
  INSTRUMENT_TYPE_TICKET,
} from '../utils/instruments';

/** An authentication device */
export interface TDevice {
  id: string;
  userId?: string;
  deviceName?: string;
  externalId?: string;
  manufacturerName?: string;
  providerName?: string;
  providerId?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  phoneNumber?: string;
  client?: string;
  dateCreated?: number;
  dateModified?: number;
}
export const restApiDevice = schema.modelEntity<TDevice>('device', {
  id: schema.id(),
  user_id: schema.reference('user').as('userId'),
  device_name: schema.string().as('deviceName'),
  external_id: schema.string().as('externalId'),
  manufacturer_name: schema.string().as('manufacturerName'),
  provider_name: schema.string().as('providerName'),
  provider_id: schema.string().as('providerId'),
  is_active: schema.boolean().as('isActive'),
  is_deleted: schema.boolean().as('isDeleted'),
  phone_number: schema.string().as('phoneNumber'),
  client: schema.string(),
  date_created: schema.rfc3339Timestamp().as('dateCreated'),
  date_modified: schema.rfc3339Timestamp().as('dateModified'),
});

/** The status of an MFA confirmation */
export type THandshakeStatus = (typeof HANDSHAKE_STATUSES)[number];
export const HANDSHAKE_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'expired',
  'cancelled',
] as const;

/**
 * Represents a confirmation request that must be completed by the user before
 * performing an action
 */
export interface THandshake {
  id: string;
  userId?: string;
  clientId?: string;
  deviceId?: string;
  sourceName?: string;
  description?: string;
  scope?: string;
  status: THandshakeStatus;
  expiresAt?: number;
  requestId?: string;
  profileId?: string;
}
export const restApiHandshake = schema.modelEntity<THandshake>('handshake', {
  id: schema.id(),
  user_id: schema.reference('user').as('userId'),
  client_id: schema.string().as('clientId'),
  device_id: schema.reference('device').as('deviceId'),
  source_name: schema.string().as('sourceName'),
  description: schema.string(),
  scope: schema.string(),
  status: schema.string().required(),
  expires_at: schema.rfc3339Timestamp().as('expiresAt'),
  request_id: schema.string().as('requestId'),
  profile_id: schema.string().as('profileId'),
});

/** API response when renewing an authentication token */
export interface TRenewedToken {
  token: string;
}
export const restApiRenewedToken = schema.object<TRenewedToken>({
  token: schema.string().required(),
});

/** API response when checking handle availability */
export interface THandleAvailability {
  available: boolean;
}
export const restApiHandleAvailability = schema.object<THandleAvailability>({
  available: schema.boolean().required(),
});

/** API response when checking email address availability */
export interface TEmailAvailability {
  email: string;
  isAvailable: boolean;
}
export const restApiEmailAvailability = schema.object<TEmailAvailability>({
  email: schema.string().required(),
  is_available: schema.boolean().required().as('isAvailable'),
});

/** API response when initiating account recovery */
export interface TAccountRecoveryDetails {
  maskedEmail?: string;
}
export const restApiAccountRecoveryDetails =
  schema.object<TAccountRecoveryDetails>({
    email: schema.string().as('maskedEmail'),
  });

/** Data associated with an individual’s Tokenized account */
export interface TUser {
  id: string;
  name?: string;
  email: string;
  phone: string;
  phoneIsVerified?: boolean;
  displayHandle: string;
  isBackedUp?: boolean;
  device?: TDevice;
  dateCreated?: number;
}
export const restApiUser = schema.modelEntity<TUser>('user', {
  id: schema.id(),
  name: schema.string(),
  email: schema.string().required(),
  phone: schema.string(),
  display_handle: schema.string().as('displayHandle'),
  is_backedup: schema.boolean().as('isBackedUp'),
  device: restApiDevice,
  date_created: schema.rfc3339Timestamp().as('dateCreated'),
});

/**
 * A representation of the user’s role within an entity they are directly associated with.
 * This includes a set of keys and other identifying information that the user (i.e.
 * natural person) has access to through his or her account. For example, a user has a
 * personal profile for acting on their own behalf as an individual entity and a
 * separate profile for their role as a director in a company. A user can only have
 * one profile per entity, but can accept roles with as many entities as they like.
 */
export interface TProfile {
  id: string;
  entityId: string;
  entityName?: string;
  handle?: string;
  displayCurrency?: string;
  individualEntityId?: string;
  individualName?: string;
  individualHandle?: string;
  originatingEntityId?: string;
  originatingEntityName?: string;
}
export const restApiProfile = schema.modelEntity<TProfile>('profile', {
  id: schema.id(),
  entity_id: schema.reference('entity').required().as('entityId'),
  entity_name: schema.string().as('entityName'),
  handle: schema.string(),
  display_currency: schema.reference('currency').as('displayCurrency'),
  individual_entity_id: schema.reference('entity').as('individualEntityId'),
  individual_name: schema.string().as('individualName'),
  individual_handle: schema.string().as('individualHandle'),
  originating_entity_id: schema.reference('entity').as('originatingEntityId'),
  originating_entity_name: schema.string().as('originatingEntityName'),
});

/** Details of an entity with signing authority over a lockbox */
export interface TSigner {
  handle?: string;
  name?: string;
  entityName?: string;
  isProposer?: boolean;
  dateAccepted?: number;
  dateRejected?: number;
}
export const restApiSigner = schema.object<TSigner>({
  handle: schema.string(),
  name: schema.string(),
  entity_name: schema.string().as('entityName'),
  is_proposer: schema.boolean().as('isProposer'),
  date_accepted: schema.rfc3339Timestamp().as('dateAccepted'),
  date_rejected: schema.rfc3339Timestamp().as('dateRejected'),
});

/**
 * The type of an entity:
 *
 * - `""`: Unspecified
 * - `"I"`: Individual
 * - `"P"`: Public company
 * - `"C"`: Private company
 * - `"L"`: Limited partnership
 * - `"X"`: Unlimited partnership
 * - `"T"`: Sole proprietorship
 * - `"S"`: Statutory company
 * - `"O"`: Non-profit organization
 * - `"N"`: Nation state
 * - `"G"`: Government agency
 * - `"U"`: Unit trust
 * - `"D"`: Discretionary trust
 */
export type TEntityType = (typeof ENTITY_TYPES)[number];
export const ENTITY_TYPES = [
  '',
  'I',
  'P',
  'C',
  'L',
  'X',
  'T',
  'S',
  'O',
  'N',
  'G',
  'U',
  'D',
] as const;

/**
 * A legal entity or ownership/business structure that owns/owes all assets and liabilities
 * within its treasury, at least for tax purposes. Examples of legal entities include natural
 * persons, companies/corporations, sovereign states, and other governmental bodies.
 * Ownership structures include discretionary and unit trusts, partnerships, and foundations.
 */
export interface TEntity {
  id: string;
  name?: string;
  handle?: string;
  entityType?: TEntityType;
  primaryVaultId?: string;
  bio?: string;
  website?: string;
  location?: string;
  avatarUrl?: string;
  signers?: TSigner[];
}
export const restApiEntity = schema.modelEntity<TEntity>('entity', {
  id: schema.id(),
  name: schema.string(),
  handle: schema.string(),
  entity_type: schema.enumerated(ENTITY_TYPES).as('entityType'),
  primary_vault_id: schema.reference('vault').as('primaryVaultId'),
  bio: schema.string(),
  website: schema.string(),
  location: schema.string(),
  avatar_url: schema.string().as('avatarUrl'),
  signers: schema.arrayOf(schema.object(restApiSigner)),
});

/** @internal */
export interface TRootKey {
  id: string;
  userId?: string;
  deviceId?: string;
  encrypted: string;
  isActive?: boolean;
  isDefault?: boolean;
  dateCreated?: number;
}
/** @internal */
export const restApiRootKey = schema.modelEntity<TRootKey>('rootKey', {
  id: schema.id().required(),
  user_id: schema.reference('user').as('userId'),
  device_id: schema.string().as('deviceId'),
  encrypted: schema.string().required(),
  is_active: schema.boolean().as('isActive'),
  is_default: schema.boolean().as('isDefault'),
  date_created: schema.rfc3339Timestamp().as('dateCreated'),
});

/**
 * A vault subdivision for organizing/grouping instruments and liabilities. Lockboxes
 * share the same control structure as the parent vault with a possible alternative
 * control structure that includes other controlling users for added flexibility.
 */
export interface TLockbox {
  id: string;
  handle?: string;
  vaultId?: string;
  entityId?: string;
  entityName?: string;
  entityHandle?: string;
  statusId?: 1 | 2 | 3 | 4 | 5 | 6;
  requiredSigners?: number;
  isActive?: boolean;
  signers: TSigner[];
}
const lockboxFields = {
  id: schema.id().required(),
  handle: schema.string(),
  vault_id: schema.id('vault').as('vaultId'),
  entity_id: schema.reference('entity').required().as('entityId'),
  entity_name: schema.string().as('entityName'),
  entity_handle: schema.string().as('entityHandle'),
  status_id: schema
    .enumeratedNumber([1, 2, 3, 4, 5, 6])
    .required()
    .as('statusId'),
  required_signers: schema.number().as('requiredSigners'),
  is_active: schema.boolean().as('isActive'),
  signers: schema.arrayOf(schema.object(restApiSigner)),
  replaced_by: schema.string().as('replacedBy'),
  replaces: schema.arrayOf(schema.string()),
};
export const restApiLockbox = schema.modelEntity<TLockbox>(
  'lockbox',
  lockboxFields,
);

/** @internal */
export interface TLockboxProposalDetails {
  path: string;
}
/** @internal */
export const restApiLockboxProposalDetails =
  schema.object<TLockboxProposalDetails>({
    path: schema.string().required(),
  });

/**
 * A set of addresses/locking scripts that share a common control method (Bitcoin script) and
 * controlling users. Instruments and liabilities controlled by the addresses/locking scripts
 * within a vault are legally owned/owed by the controlling entity. A vault may be a collection
 * of N of M multi-key locking scripts with addresses that have been derived from a
 * parent/root key using a hierarchical deterministic method like BIP-32.
 */
export interface TVault {
  id: string;
  entityId: string;
  primaryLockboxId?: string;
  name?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  dateCreated?: number;
  dateModified?: number;
}
export const restApiVault = schema.modelEntity<TVault>('vault', {
  id: schema.id(),
  entity_id: schema.reference('entity').required().as('entityId'),
  primary_lockbox_id: schema.reference('lockbox').as('primaryLockboxId'),
  name: schema.string(),
  is_active: schema.boolean().as('isActive'),
  is_deleted: schema.boolean().as('isDeleted'),
  date_created: schema.rfc3339Timestamp().as('dateCreated'),
  date_modified: schema.rfc3339Timestamp().as('dateModified'),
});

/** The quantity of one instrument held in a lockbox */
export interface TInstrumentBalance {
  instrumentId?: string;
  totalUnits: number;
  availableUnits?: number;
}
/** The instrument quantities held in a lockbox */
export interface TLockboxBalanceList {
  lockboxId: string;
  balances: TInstrumentBalance[];
}
export const schemaLockboxBalanceList = schema.object<TLockboxBalanceList>({
  lock_box_id: schema.string().as('lockboxId'),
  balances: schema.arrayOf(
    schema.object<TInstrumentBalance>({
      instrument_id: schema.reference('instrument').as('instrumentId'),
      total_units: schema.number().required().as('totalUnits'),
      available_units: schema.number().required().as('availableUnits'),
    }),
  ),
});

/** A list of the net equity values of lockboxes over a time range */
export interface TNetEquityHistory {
  start?: number;
  end?: number;
  values?: {
    date?: number;
    lockboxId: string;
    balance: number;
  }[];
}
export const restApiNetEquityHistory = schema.object<TNetEquityHistory>({
  start: schema.rfc3339Timestamp(),
  end: schema.rfc3339Timestamp(),
  values: schema.arrayOf(
    schema.object({
      date: schema.rfc3339Timestamp().required(),
      lock_box_id: schema.string().required().as('lockboxId'),
      balance: schema.number().required(),
    }),
  ),
});

/** The type of a counterparty contact */
export type TCounterpartyContactType =
  (typeof COUNTERPARTY_CONTACT_TYPES)[number];
export const COUNTERPARTY_CONTACT_TYPES = [
  'bsvalias',
  'peer_channel',
  'unknown',
] as const;

/** Relationship flags for contacts */
export type TRelationshipFlag = (typeof RELATIONSHIP_FLAGS)[number];
export const RELATIONSHIP_FLAGS = ['contact', 'hidden'] as const;

/** The public details associated with a Tokenized handle */
export interface TContact {
  id?: string;
  relationshipFlags?: TRelationshipFlag[];
  displayName?: string;
  displayHandle?: string;
  counterpartyType?: TCounterpartyContactType;
  entityType?: TEntityType;
  bio?: string;
  website?: string;
  location?: string;
  avatarUrl?: string;
  dateCreated?: number;
}
export const restApiHandleContact = schema.object<TContact>({
  id: schema.string().as('id'),
  name: schema.string().as('displayName'),
  handle: schema.string().as('displayHandle'),
  entity_type: schema.enumerated(ENTITY_TYPES).as('entityType'),
  bio: schema.string(),
  website: schema.string(),
  location: schema.string(),
  avatar_url: schema.string().as('avatarUrl'),
});
export const restApiRelationshipContact = schema.object<TContact>({
  id: schema.string().as('id'),
  flags: schema
    .arrayOf(schema.enumerated(RELATIONSHIP_FLAGS))
    .as('relationshipFlags'),
  display_name: schema.string().as('displayName'),
  display_handle: schema.string().as('displayHandle'),
  entity_type: schema.enumerated(ENTITY_TYPES).as('entityType'),
  bio: schema.string(),
  website: schema.string(),
  location: schema.string(),
  avatar_url: schema.string().as('avatarUrl'),
  date_created: schema.rfc3339Timestamp().as('dateCreated'),
});
export const restApiCounterpartyContact = schema.object<TContact>({
  id: schema.string().as('id'),
  flags: schema
    .arrayOf(schema.enumerated(RELATIONSHIP_FLAGS))
    .as('relationshipFlags'),
  display_name: schema.string().as('displayName'),
  value: schema.string().as('displayHandle'),
  counterparty_type: schema.string().as('counterpartyType'),
});

/**
 * Instrument details for a currency (the endpoint is deprecated,
 * but the interface is still used inside Instrument)
 */
export interface TCurrencyDetails {
  currencyCode?: string;
  instrumentId?: string;
  name?: string;
  symbol?: string;
  unitPrecision?: number;
}

/**
 * The rate of exchange between two currencies. See the
 * ExchangeRates class for the public interface.
 * @internal
 */
export interface TCurrencyExchange {
  from: string;
  to: string;
  rate: number;
}
/** @internal */
export const restApiCurrencyExchangeRates = schema.arrayOf(
  schema.object<TCurrencyExchange>({
    from: schema.string().required(),
    to: schema.string().required(),
    rate: schema.number().required(),
  }),
);

/** The rate of exchange between two currencies */
export interface TCurrencyExchangeRate {
  rate: number;
}
export const restApiCurrencyExchangeRate = schema.object<TCurrencyExchangeRate>(
  {
    rate: schema.number().required(),
  },
);

/** The face value an instrument */
export interface TFaceValue {
  units?: number;
  currency?: string;
  unitPrecision?: number;
}
export const restApiFaceValue = schema.object<TFaceValue>({
  units: schema.number(),
  currency: schema.reference('currency'),
  unit_precision: schema.number().as('unitPrecision'),
});

/** Age restrictions for an instrument */
export interface TAgeRestriction {
  lower?: number;
  upper?: number;
}
export const restApiAgeRestriction = schema.object<TAgeRestriction>({
  lower: schema.number(),
  upper: schema.number(),
});

/** Instrument details specific to currencies */
export interface TInstrumentCCYSpecifics extends Record<string, unknown> {
  currencyCode: string;
  precision: number;
  monetaryAuthority?: string;
}
export const restApiInstrumentCCYSpecifics =
  schema.object<TInstrumentCCYSpecifics>({
    monetary_authority: schema.string().as('monetaryAuthority'),
    currency_code: schema.reference('currency').as('currencyCode'),
    precision: schema.number(),
  });

/** Instrument details specific to credit notes */
export interface TInstrumentCRNSpecifics extends Record<string, unknown> {
  name?: string;
  faceValue?: TFaceValue;
  expirationTimestamp?: number;
  transfersPermitted?: boolean;
}
export const restApiInstrumentCRNSpecifics =
  schema.object<TInstrumentCRNSpecifics>({
    name: schema.string().as('name'),
    face_value: schema.object(restApiFaceValue).as('faceValue'),
    expiration_timestamp: schema
      .nanosecondTimestamp()
      .as('expirationTimestamp'),
    transfers_permitted: schema.boolean().as('transfersPermitted'),
  });

/** Instrument details specific to discount coupons */
export interface TInstrumentCOUSpecifics extends Record<string, unknown> {
  couponName?: string;
  redemptionVenue?: string;
  details?: string;
  // Deprecated: new discount coupons have no face value
  faceValue?: TFaceValue;
  expirationTimestamp?: number;
  transfersPermitted?: boolean;
  validFromTimestamp?: number;
  redeemingEntity?: string;
}
export const restApiInstrumentCOUSpecifics =
  schema.object<TInstrumentCOUSpecifics>({
    coupon_name: schema.string().as('couponName'),
    redemption_venue: schema.string().as('redemptionVenue'),
    details: schema.string(),
    face_value: schema.object(restApiFaceValue).as('faceValue'),
    expiration_timestamp: schema
      .nanosecondTimestamp()
      .as('expirationTimestamp'),
    transfers_permitted: schema.boolean().as('transfersPermitted'),
    valid_from_timestamp: schema.nanosecondTimestamp().as('validFromTimestamp'),
    redeeming_entity: schema.string().as('redeemingEntity'),
  });

/** Instrument details specific to loyalty points */
export interface TInstrumentLOYSpecifics extends Record<string, unknown> {
  programName?: string;
  details?: string;
  expirationTimestamp?: number;
  transfersPermitted?: boolean;
  ageRestriction?: TAgeRestriction;
}
export const restApiInstrumentLOYSpecifics =
  schema.object<TInstrumentLOYSpecifics>({
    program_name: schema.string().as('programName'),
    details: schema.string(),
    expiration_timestamp: schema
      .nanosecondTimestamp()
      .as('expirationTimestamp'),
    transfers_permitted: schema.boolean().as('transfersPermitted'),
    age_restriction: schema.object(restApiAgeRestriction).as('ageRestriction'),
  });

/** Instrument details specific to reward points */
export interface TInstrumentRPTSpecifics extends Record<string, unknown> {
  programName?: string;
  details?: string;
  expirationTimestamp?: number;
  transfersPermitted?: boolean;
  ageRestriction?: TAgeRestriction;
}
export const restApiInstrumentRPTSpecifics =
  schema.object<TInstrumentRPTSpecifics>({
    program_name: schema.string().as('programName'),
    details: schema.string(),
    expiration_timestamp: schema
      .nanosecondTimestamp()
      .as('expirationTimestamp'),
    transfers_permitted: schema.boolean().as('transfersPermitted'),
    age_restriction: schema.object(restApiAgeRestriction).as('ageRestriction'),
  });

/** Instrument details specific to casino chips */
export interface TInstrumentCHPSpecifics extends Record<string, unknown> {
  casinoName?: string;
  useType?: 'R' | 'F';
  faceValue?: TFaceValue;
  expirationTimestamp?: number;
  transfersPermitted?: boolean;
  ageRestriction?: TAgeRestriction;
}
export const restApiInstrumentCHPSpecifics =
  schema.object<TInstrumentCHPSpecifics>({
    casino_name: schema.string().as('casinoName'),
    use_type: schema.reference('casinoChipType').as('useType'),
    face_value: schema.object(restApiFaceValue).as('faceValue'),
    expiration_timestamp: schema
      .nanosecondTimestamp()
      .as('expirationTimestamp'),
    transfers_permitted: schema.boolean().as('transfersPermitted'),
    age_restriction: schema.object(restApiAgeRestriction).as('ageRestriction'),
  });

/** Instrument details specific to event tickets */
export interface TInstrumentTICSpecifics extends Record<string, unknown> {
  eventName?: string;
  venue?: string;
  eventStartTimestamp?: number;
  eventEndTimestamp?: number;
  area?: string;
  section?: string;
  row?: string;
  seat?: string;
  details?: string;
  transfersPermitted?: boolean;
  ageRestriction?: TAgeRestriction;
}
export const restApiInstrumentTICSpecifics =
  schema.object<TInstrumentTICSpecifics>({
    event_name: schema.string().as('eventName'),
    venue: schema.string(),
    event_start_timestamp: schema
      .nanosecondTimestamp()
      .as('eventStartTimestamp'),
    event_end_timestamp: schema.nanosecondTimestamp().as('eventEndTimestamp'),
    area: schema.string(),
    section: schema.string(),
    row: schema.string(),
    seat: schema.string(),
    details: schema.string(),
    transfers_permitted: schema.boolean().as('transfersPermitted'),
    age_restriction: schema.object(restApiAgeRestriction).as('ageRestriction'),
  });

/** Details for an instrument (defined in its contract) */
export interface TInstrumentDetails {
  instrumentId: string;
  contractAddress?: string;
  currentInstrumentStateTxId?: string;
  opReturn?: string;
  instrumentType?: string;
  isActive?: boolean;
  isVerified?: boolean;
  freezePeriod?: number;
  freezeTxId?: string;
  authorizedQuantity?: number;
  issuerName?: string;
  issuerId?: string;
  issuerHandle?: string;
  instrumentTypeSpecifics?: Record<string, unknown>;
  contractFee?: number;
  transferFee?: number;
  transferFeeInstrumentId?: string;
  dateCreated?: number;
  dateModified?: number;
}
/** Details for a currency instrument */
export interface TInstrumentDetailsCurrency extends TInstrumentDetails {
  instrumentType: typeof INSTRUMENT_TYPE_CURRENCY;
  instrumentTypeSpecifics?: TInstrumentCCYSpecifics;
}
/** Details for a credit note instrument */
export interface TInstrumentDetailsCreditNote extends TInstrumentDetails {
  instrumentType: typeof INSTRUMENT_TYPE_CREDIT_NOTE;
  instrumentTypeSpecifics?: TInstrumentCRNSpecifics;
}
/** Details for a discount coupon instrument */
export interface TInstrumentDetailsCoupon extends TInstrumentDetails {
  instrumentType: typeof INSTRUMENT_TYPE_COUPON;
  instrumentTypeSpecifics?: TInstrumentCOUSpecifics;
}
/** Details for a loyalty point instrument */
export interface TInstrumentDetailsLoyalty extends TInstrumentDetails {
  instrumentType: typeof INSTRUMENT_TYPE_LOYALTY;
  instrumentTypeSpecifics?: TInstrumentLOYSpecifics;
}
/** Details for a reward point instrument */
export interface TInstrumentDetailsReward extends TInstrumentDetails {
  instrumentType: typeof INSTRUMENT_TYPE_REWARD;
  instrumentTypeSpecifics?: TInstrumentRPTSpecifics;
}
/** Details for a casino chip instrument */
export interface TInstrumentDetailsCasino extends TInstrumentDetails {
  instrumentType: typeof INSTRUMENT_TYPE_CASINO;
  instrumentTypeSpecifics?: TInstrumentCHPSpecifics;
}
/** Details for an event ticket instrument */
export interface TInstrumentDetailsTicket extends TInstrumentDetails {
  instrumentType: typeof INSTRUMENT_TYPE_TICKET;
  instrumentTypeSpecifics?: TInstrumentTICSpecifics;
}
export const restApiInstrumentDetails = schema.modelEntity<TInstrumentDetails>(
  'instrumentDetails',
  {
    instrument_id: schema.id('instrument').as('instrumentId'),
    contract_address: schema.reference('contract').as('contractAddress'),
    current_instrument_state_txid: schema
      .string()
      .as('currentInstrumentStateTxId'),
    op_return: schema.string().as('opReturn'),
    instrument_type: schema.reference('instrumentType').as('instrumentType'),
    is_active: schema.boolean().as('isActive'),
    is_verified: schema.boolean().as('isVerified'),
    freeze_period: schema.rfc3339Timestamp().as('freezePeriod'),
    freeze_txid: schema.string().as('freezeTxId'),
    authorized_quantity: schema.number().as('authorizedQuantity'),
    issuer_name: schema.string().as('issuerName'),
    issuer_id: schema.reference('entity').as('issuerId'),
    issuer_handle: schema.string().as('issuerHandle'),
    instrument_type_specifics: schema
      .selectTypeViaKey('instrumentType', {
        [INSTRUMENT_TYPE_CURRENCY]: restApiInstrumentCCYSpecifics,
        [INSTRUMENT_TYPE_CREDIT_NOTE]: restApiInstrumentCRNSpecifics,
        [INSTRUMENT_TYPE_COUPON]: restApiInstrumentCOUSpecifics,
        [INSTRUMENT_TYPE_LOYALTY]: restApiInstrumentLOYSpecifics,
        [INSTRUMENT_TYPE_REWARD]: restApiInstrumentRPTSpecifics,
        [INSTRUMENT_TYPE_CASINO]: restApiInstrumentCHPSpecifics,
        [INSTRUMENT_TYPE_TICKET]: restApiInstrumentTICSpecifics,
      })
      .as('instrumentTypeSpecifics'),
    contract_fee: schema.number().as('contractFee'),
    transfer_fee: schema.number().as('transferFee'),
    transfer_fee_instrument_id: schema
      .id('instrument')
      .as('transferFeeInstrumentId'),
    date_created: schema.rfc3339Timestamp().as('dateCreated'),
    date_modified: schema.rfc3339Timestamp().as('dateModified'),
  },
);

/** The type of an activity event */
export type TActivityType = (typeof ACTIVITY_TYPES)[number];
export const ACTIVITY_TYPES = [
  'payment',
  'transfer_fee',
  'dust',
  'trade_offer',
  'bank_transfer',
  'contract_formation',
  'contract_amendment',
  'contract_expire',
  'instrument_creation',
  'instrument_amendment',
  'instrument_expire',
  'lock_box_creation_proposal',
  'lock_box_deletion_proposal',
  'reorg',
  'bank_account_creation',
  'bank_account_deletion',
] as const;

/** The stage of an organization modification */
export type TReorgActivityStage = (typeof ACTIVITY_TYPES)[number];
export const REORG_ACTIVITY_STAGES = ['lockbox_creation', 'migration'] as const;

export type TActivityOverviewType = (typeof ACTIVITY_OVERVIEW_TYPES)[number];
export const ACTIVITY_OVERVIEW_TYPES = [
  'transaction',
  'request',
  'lock_box',
  'contract',
] as const;

/** The stage reached by an activity event */
export type TActivityStage = (typeof ACTIVITY_STAGES)[number];
export const ACTIVITY_STAGES = [
  'connecting',
  'confirming',
  'proposal',
  'reviewing',
  'accepting',
  'finalizing',
  'executed',
] as const;

/** The reason for an activity event terminating unsuccessfully */
export type TTerminationReason = (typeof TERMINATION_REASONS)[number];
export const TERMINATION_REASONS = [
  'cancelled',
  'revoked',
  'rejected',
  'expired',
  'failed',
] as const;

export type TActivityTransactionType = (typeof TRANSACTION_TYPES)[number];
export const TRANSACTION_TYPES = ['request', 'pending_tx', 'tx'] as const;

/**
 * One signer of a proposed activity event transaction, and whether they
 * have accepted (signed) or rejected the proposal
 */
export interface TActivityTransactionSigner extends TSigner {
  id?: string;
  dateSigned?: number;
}

/** A transaction that needs to be signed to complete an activity event */
export interface TActivityTransaction {
  type?: TActivityTransactionType;
  isInternal?: boolean;
  is2StageExchange?: boolean;
  pendingTransactionId?: string;
  txId?: string;
  requestId?: string;
  isProposer?: boolean;
  dateSigned?: number;
  dateRejected?: number;
  dateCancelled?: number;
  dateComplete?: number;
  signers?: TActivityTransactionSigner[];
}

/** Transfer of a quantity of an instrument as part of an activity event */
export interface TActivityTransfer {
  instrumentId?: string;
  direction?: 'received' | 'sent';
  quantity?: number;
  feeType?: 'transfer';
}

/**
 * Identifies another entity involved in an activity event, and lists any
 * instrument quantities transferred to them
 */
export interface TActivityCounterparty {
  povLockboxId?: string;
  displayHandle?: string;
  displayName?: string;
  isAnonymous?: boolean;
  isInternal?: boolean;
  isVerified?: boolean;
  transfers?: TActivityTransfer[];
}

/** Details of the contract modified by an activity event */
export interface TActivityContractDetails {
  contractAddress?: string;
  name?: string;
}

/** Details of an instrument modified by an activity event */
export interface TActivityInstrumentDetails {
  instrumentId?: string;
  name?: string;
  unitsTotal: number;
  unitsDelta: number;
}

export type TFeeType = (typeof FEE_TYPES)[number];
export const FEE_TYPES = ['transfer'] as const;

export interface TFeePaidInInstruments {
  feeType: TFeeType;
  instrumentId: string;
  quantity: number;
}
/** @internal */
export const schemaFeePaidInInstruments = schema.object<TFeePaidInInstruments>({
  fee_type: schema.enumerated(FEE_TYPES).as('feeType'),
  instrument_id: schema.reference('instrument').as('instrumentId'),
  quantity: schema.number(),
});

/** A breakdown of the network fees incurred by an activity event */
export interface TActivityFees {
  agentFees?: number;
  agentFeesTotal?: number;
  dustCreated?: number;
  dustCreatedTotal?: number;
  dustDestroyed?: number;
  dustDestroyedTotal?: number;
  dustNet?: number;
  dustTotal?: number;
  miningFees?: number;
  miningFeesTotal?: number;
  transferFees?: number;
  transferFeesTotal?: number;
  inInstruments?: TFeePaidInInstruments[];
  inInstrumentsTotal?: TFeePaidInInstruments[];
}

/** Minimal details about an activity event */
export interface TActivityOverview {
  activityId: string;
  lockboxId: string;
  stage?: TActivityStage;
  terminationReason?: TTerminationReason;
  activityType: TActivityOverviewType;
  dateCreated?: number;
  dateModified?: number;
}

export const restApiActivityOverview = schema.object<TActivityOverview>({
  activity_id: schema.id('activity').as('activityId'),
  lock_box_id: schema.reference('lockbox').as('lockboxId'),
  activity_type: schema.enumerated(ACTIVITY_OVERVIEW_TYPES).as('activityType'),
  stage: schema.enumerated(ACTIVITY_STAGES),
  termination_reason: schema
    .enumerated(TERMINATION_REASONS)
    .as('terminationReason'),
  date_created: schema.rfc3339Timestamp().as('dateCreated'),
  date_modified: schema.rfc3339Timestamp().as('dateModified'),
});

/** Details of an activity event */
export interface TActivityDetails {
  id: string;
  lockboxId?: string;
  activityEventType?: TActivityType;
  reorgStage?: TReorgActivityStage;
  stage?: TActivityStage;
  isInitiator?: boolean;
  terminationReason?: TTerminationReason;
  transactions?: TActivityTransaction[];
  counterparties?: TActivityCounterparty[];
  contract?: TActivityContractDetails;
  instruments?: TActivityInstrumentDetails[];
  lockbox?: TLockbox;
  memo?: string;
  fees: TActivityFees;
  dateCreated?: number;
  dateModified?: number;
}

export const restApiActivityDetails = schema.object<TActivityDetails>({
  action_thread_id: schema.id('activity').as('id'),
  lock_box_id: schema.reference('lockbox').as('lockboxId'),
  activity_event_type: schema
    .enumerated(ACTIVITY_TYPES)
    .as('activityEventType'),
  reorg_stage: schema.enumerated(REORG_ACTIVITY_STAGES).as('reorgStage'),
  stage: schema.enumerated(ACTIVITY_STAGES),
  is_initiator: schema.boolean().as('isInitiator'),
  termination_reason: schema
    .enumerated(TERMINATION_REASONS)
    .as('terminationReason'),
  transactions: schema.arrayOf(
    schema.object({
      type: schema.enumerated(TRANSACTION_TYPES),
      is_internal: schema.boolean().as('isInternal'),
      is_2_stage_exchange: schema.boolean().as('is2StageExchange'),
      pending_transaction_id: schema
        .reference('pendingTransaction')
        .as('pendingTransactionId'),
      txid: schema.string().as('txId'),
      request_id: schema.string().as('requestId'),
      is_proposer: schema.boolean().as('isProposer'),
      date_signed: schema.rfc3339Timestamp().as('dateSigned'),
      date_rejected: schema.rfc3339Timestamp().as('dateRejected'),
      date_cancelled: schema.rfc3339Timestamp().as('dateCancelled'),
      date_complete: schema.rfc3339Timestamp().as('dateComplete'),
      signers: schema.arrayOf(
        schema.object({
          id: schema.string(),
          handle: schema.string(),
          name: schema.string(),
          entity_name: schema.string().as('entityName'),
          is_proposer: schema.boolean().as('isProposer'),
          date_accepted: schema.rfc3339Timestamp().as('dateAccepted'),
          date_rejected: schema.rfc3339Timestamp().as('dateRejected'),
          date_signed: schema.rfc3339Timestamp().as('dateSigned'),
        }),
      ),
    }),
  ),
  counterparties: schema.arrayOf(
    schema.object({
      lock_box_id: schema.string().as('lockboxId'),
      pov_lock_box_id: schema.string().as('povLockboxId'),
      display_handle: schema.string().as('displayHandle'),
      display_name: schema.string().as('displayName'),
      is_anonymous: schema.boolean().as('isAnonymous'),
      is_internal: schema.boolean().as('isInternal'),
      is_verified: schema.boolean().as('isVerified'),
      transfers: schema.arrayOf(
        schema.object({
          instrument_id: schema.reference('instrument').as('instrumentId'),
          direction: schema.enumerated(['received', 'sent']),
          quantity: schema.number(),
          fee_type: schema.enumerated(FEE_TYPES).as('feeType'),
        }),
      ),
    }),
  ),
  contract: schema.object({
    address: schema.reference('contract').as('contractAddress'),
    name: schema.string(),
  }),
  instruments: schema
    .arrayOf(
      schema.object({
        instrument_id: schema.reference('instrument').as('instrumentId'),
        name: schema.string(),
        units_total: schema.number().as('unitsTotal'),
        units_delta: schema.number().as('unitsDelta'),
      }),
    )
    .as('instruments'),
  lock_box: schema.object(lockboxFields).as('lockbox'),
  memo: schema.string(),
  fees: schema.object({
    agent_fees: schema.number().as('agentFees'),
    agent_fees_total: schema.number().as('agentFeesTotal'),
    dust_created: schema.number().as('dustCreated'),
    dust_created_total: schema.number().as('dustCreatedTotal'),
    dust_destroyed: schema.number().as('dustDestroyed'),
    dust_destroyed_total: schema.number().as('dustDestroyedTotal'),
    dust_net: schema.number().as('dustNet'),
    dust_total: schema.number().as('dustTotal'),
    mining_fees: schema.number().as('miningFees'),
    mining_fees_total: schema.number().as('miningFeesTotal'),
    transfer_fees: schema.number().as('transferFees'),
    transfer_fees_total: schema.number().as('transferFeesTotal'),
    in_instruments: schema
      .arrayOf(schemaFeePaidInInstruments)
      .as('inInstruments'),
    in_instruments_total: schema
      .arrayOf(schemaFeePaidInInstruments)
      .as('inInstrumentsTotal'),
  }),
  date_created: schema.rfc3339Timestamp().as('dateCreated'),
  date_modified: schema.rfc3339Timestamp().as('dateModified'),
});

/** @internal */
export interface TPendingTransactionSignature {
  signatureIndex: number;
  rootKeyId: string;
  derivationPath: string;
}
/** @internal */
export const schemaPendingTransactionSignature =
  schema.object<TPendingTransactionSignature>({
    signature_index: schema.number().required().as('signatureIndex'),
    root_key_id: schema.reference('rootKey').required().as('rootKeyId'),
    derivation_path: schema.string().required().as('derivationPath'),
  });

/** @internal */
export interface TPendingTransaction {
  id: string;
  requestId?: string;
  activityId?: string;
  tx: string;
  inputSupplements: {
    lockingScript: string;
    value: number;
    neededSignatures?: schema.OutputTypeFromSchemaValue<
      typeof schemaPendingTransactionSignature
    >[];
    previousTransactionId?: string;
  }[];
  outputSupplements: {
    isRemainder: boolean;
    isDust: boolean;
    neededSignatures?: schema.OutputTypeFromSchemaValue<
      typeof schemaPendingTransactionSignature
    >[];
  }[];
}
/** @internal */
export const restApiPendingTransaction =
  schema.modelEntity<TPendingTransaction>('pendingTransaction', {
    id: schema.id('pendingTransaction').required(),
    request_id: schema.string().as('requestId'),
    activity_id: schema.reference('activity').as('activityId'),
    tx: schema.string().required(),
    input_supplements: schema
      .arrayOf(
        schema.object({
          locking_script: schema.string().as('lockingScript'),
          value: schema.number().required(),
          needed_signatures: schema
            .arrayOf(schemaPendingTransactionSignature)
            .as('neededSignatures'),
          previous_transaction_id: schema.string().as('previousTransactionId'),
        }),
      )
      .required()
      .as('inputSupplements'),
    output_supplements: schema
      .arrayOf(
        schema.object({
          is_remainder: schema.boolean().as('isRemainder'),
          is_dust: schema.boolean().as('isDust'),
          needed_signatures: schema
            .arrayOf(schemaPendingTransactionSignature)
            .as('neededSignatures'),
        }),
      )
      .required()
      .as('outputSupplements'),
  });

/** A response from an action indicating an activity event has been created */
export interface TNewActivityCreated {
  handshakeId: string;
  activityId: string;
}
export const restApiNewActivityCreated = schema.object<TNewActivityCreated>({
  handshake_id: schema.string().as('handshakeId'),
  // activity_id will be present when mobile signing is used
  activity_id: schema.string().as('activityId'),
});

/**
 * A response from a non-activity-related operation (like updating email or
 * phone number) indicating a handshake is required
 */
export interface THandshakeRequiredNoActivity {
  handshakeId: string;
}
export const restApiHandshakeRequiredNoActivity =
  schema.object<THandshakeRequiredNoActivity>({
    id: schema.string().required().as('handshakeId'),
  });

const restApiIssuerProtocolData = schema.object({
  Name: schema.string().as('name'),
  Type: schema.enumerated(ENTITY_TYPES).as('type'),
  DomainName: schema.string().as('domainName'),
});

const restApiContractProtocolData = schema.object({
  ContractName: schema.string().as('contractName'),
  BodyOfAgreementType: schema.number().as('bodyOfAgreementType'),
  ContractExpiration: schema.nanosecondTimestamp().as('contractExpiration'),
  Issuer: schema.object(restApiIssuerProtocolData).as('issuer'),
  ContractFee: schema.number().as('contractFee'),
  MasterAddress: schema.reference('address').as('masterAddress'),
  Timestamp: schema.nanosecondTimestamp().as('timestamp'),
  OperatorEntityContract: schema
    .reference('address')
    .as('operatorEntityContract'),
  AdminAddress: schema.reference('address').as('adminAddress'),
  OperatorAddress: schema.reference('address').as('operatorAddress'),
  GoverningLaw: schema.reference('legalSystem').as('governingLaw'),
  Jurisdiction: schema.reference('polity').as('jurisdiction'),
});

const restApiContractClause = {
  id: schema.string(),
  Title: schema.string().as('title'),
  Body: schema.string().as('body'),
  Children: schema.arrayOf(schema.object({})),
};
// Recursive hierarchy
restApiContractClause.Children = schema
  .arrayOf(
    schema.modelEntity('clause', restApiContractClause, {
      idOptional: true,
    }),
  )
  .as('children');

const restApiContractBodyProtocolData = schema.object({
  Chapters: schema
    .arrayOf(
      schema.object({
        id: schema.string(),
        Title: schema.string().as('title'),
        Preamble: schema.string().as('preamble'),
        Articles: schema
          .arrayOf(schema.object(restApiContractClause))
          .as('articles'),
      }),
    )
    .as('chapters'),
  Definitions: schema
    .arrayOf(
      schema.object({
        id: schema.string(),
        Term: schema.string().as('term'),
        Definition: schema.string().as('definition'),
      }),
    )
    .as('definitions'),
  Revision: schema.number().as('revision'),
  Timestamp: schema.nanosecondTimestamp().as('timestamp'),
});

export interface TTransferFee {
  address?: string;
  quantity?: number;
  useCurrentInstrument?: boolean;
  contract?: string;
  instrumentCode?: string;
}
const restApiTransferFee = schema.object({
  Address: schema.string().as('address'),
  Quantity: schema.number().as('quantity'),
  UseCurrentInstrument: schema.boolean().as('useCurrentInstrument'),
  Contract: schema.string().as('contract'),
  InstrumentCode: schema.string().as('instrumentCode'),
});

const restApiInstrumentProtocolData = schema.object({
  InstrumentType: schema.reference('instrumentType').as('instrumentType'),
  AuthorizedTokenQty: schema.number().as('authorizedQuantity'),
  InstrumentRevision: schema.number().as('instrumentRevision'),
  Timestamp: schema.nanosecondTimestamp().as('timestamp'),
  TradeRestrictions: schema
    .arrayOf(schema.reference('polity'))
    .as('tradeRestrictions'),
  InstrumentIndex: schema.number().as('instrumentIndex'),
  InstrumentPermissions: schema.string().as('instrumentPermissions'),
  EnforcementOrdersPermitted: schema.boolean().as('enforcementOrdersPermitted'),
  VotingRights: schema.boolean().as('votingRights'),
  VoteMultiplier: schema.number().as('voteMultiplier'),
  AdministrationProposal: schema.boolean().as('administrationProposal'),
  HolderProposal: schema.boolean().as('holderProposal'),
  InstrumentModificationGovernance: schema
    .number()
    .as('instrumentModificationGovernance'),
  TransferFee: schema.object(restApiTransferFee).as('transferFee'),
});

const restApiFaceValueProtocolData = schema.object({
  Value: schema.number().as('units'),
  CurrencyCode: schema.reference('currency').as('currency'),
  Precision: schema.number().as('unitPrecision'),
});

const restApiAgeRestrictionProtocolData = schema.object({
  Lower: schema.number().as('lower'),
  Upper: schema.number().as('upper'),
});

const restApiInstrumentCCYProtocolData = schema.object({
  MonetaryAuthority: schema.string().as('monetaryAuthority'),
  CurrencyCode: schema.reference('currency').as('currencyCode'),
  Precision: schema.number().as('precision'),
});

const restApiInstrumentCRNProtocolData = schema.object({
  Name: schema.string().as('name'),
  FaceValue: schema.object(restApiFaceValueProtocolData).as('faceValue'),
  ExpirationTimestamp: schema.nanosecondTimestamp().as('expirationTimestamp'),
  TransfersPermitted: schema.boolean().as('transfersPermitted'),
});

const restApiInstrumentCOUProtocolData = schema.object({
  CouponName: schema.string().as('couponName'),
  RedemptionVenue: schema.string().as('redemptionVenue'),
  Details: schema.string().as('details'),
  FaceValue: schema.object(restApiFaceValueProtocolData).as('faceValue'),
  ExpirationTimestamp: schema.nanosecondTimestamp().as('expirationTimestamp'),
  TransfersPermitted: schema.boolean().as('transfersPermitted'),
  ValidFromTimestamp: schema.nanosecondTimestamp().as('validFromTimestamp'),
  RedeemingEntity: schema.string().as('redeemingEntity'),
});

const restApiInstrumentLOYProtocolData = schema.object({
  ProgramName: schema.string().as('programName'),
  Details: schema.string().as('details'),
  ExpirationTimestamp: schema.nanosecondTimestamp().as('expirationTimestamp'),
  TransfersPermitted: schema.boolean().as('transfersPermitted'),
  AgeRestriction: schema
    .object(restApiAgeRestrictionProtocolData)
    .as('ageRestriction'),
});

const restApiInstrumentRPTProtocolData = schema.object({
  ProgramName: schema.string().as('programName'),
  Details: schema.string().as('details'),
  ExpirationTimestamp: schema.nanosecondTimestamp().as('expirationTimestamp'),
  TransfersPermitted: schema.boolean().as('transfersPermitted'),
  AgeRestriction: schema
    .object(restApiAgeRestrictionProtocolData)
    .as('ageRestriction'),
});

const restApiInstrumentCHPProtocolData = schema.object({
  CasinoName: schema.string().as('casinoName'),
  UseType: schema.reference('casinoChipType').as('useType'),
  FaceValue: schema.object(restApiFaceValueProtocolData).as('faceValue'),
  ExpirationTimestamp: schema.nanosecondTimestamp().as('expirationTimestamp'),
  TransfersPermitted: schema.boolean().as('transfersPermitted'),
  AgeRestriction: schema
    .object(restApiAgeRestrictionProtocolData)
    .as('ageRestriction'),
});

const restApiInstrumentTICProtocolData = schema.object({
  EventName: schema.string().as('eventName'),
  Venue: schema.string().as('venue'),
  EventStartTimestamp: schema.nanosecondTimestamp().as('eventStartTimestamp'),
  EventEndTimestamp: schema.nanosecondTimestamp().as('eventEndTimestamp'),
  Area: schema.string().as('area'),
  Section: schema.string().as('section'),
  Row: schema.string().as('row'),
  Seat: schema.string().as('seat'),
  Details: schema.string().as('details'),
  TransfersPermitted: schema.boolean().as('transfersPermitted'),
  AgeRestriction: schema
    .object(restApiAgeRestrictionProtocolData)
    .as('ageRestriction'),
});

const restApiBodyOfAgreement = schema.object({
  txid: schema.string().as('txId'),
  details: schema.object({
    Chapters: schema
      .arrayOf(
        schema.modelEntity(
          'clause',
          {
            id: schema.id(),
            Title: schema.string().as('title'),
            Preamble: schema.string().as('preamble'),
            Articles: schema
              .arrayOf(
                schema.modelEntity('clause', restApiContractClause, {
                  idOptional: true,
                }),
              )
              .as('articles'),
          },
          { idOptional: true },
        ),
      )
      .as('chapters'),
    Definitions: schema
      .arrayOf(
        schema.modelEntity(
          'clause',
          {
            id: schema.id(),
            Term: schema.string().as('term'),
            Definition: schema.string().as('definition'),
          },
          { idOptional: true },
        ),
      )
      .as('definitions'),
    Timestamp: schema.nanosecondTimestamp().as('timestamp'),
  }),
});

export const restApiContract = schema.modelEntity(
  'contract',
  {
    contract_address: schema.id().as('contractAddress'),
    contract_type: schema.reference('contractType').as('contractType'),
    name: schema.string(),
    txid: schema.string().as('txId'),
    freeze_period: schema.rfc3339Timestamp().as('freezePeriod'),
    freeze_txid: schema.string().as('freezeTxId'),
    admin_is_verified: schema.boolean().as('adminIsVerified'),
    operator_is_verified: schema.boolean().as('operatorIsVerified'),
    administrator_address: schema
      .reference('address')
      .as('administratorAddress'),
    operator_address: schema.reference('address').as('operatorAddress'),
    master_address: schema.reference('address').as('masterAddress'),
    issuer: restApiIssuerProtocolData,
    issuer_handle: schema.string().as('issuerHandle'),
    details: restApiContractProtocolData,
    body_of_agreement: schema
      .object(restApiBodyOfAgreement)
      .as('bodyOfAgreement'),
    draft: schema.modelEntity('contractDraft', {
      id: schema.id(),
      lock_box_id: schema.string().as('lockboxId'),
      details: restApiContractProtocolData,
      body_of_agreement: schema
        .object(restApiBodyOfAgreement)
        .as('bodyOfAgreement'),
      is_subscribed: schema.boolean().as('isSubscribed'),
      is_posted: schema.boolean().as('isPosted'),
      all_instruments_posted: schema.boolean().as('allInstrumentsPosted'),
      was_rejected: schema.boolean().as('wasRejected'),
      date_created: schema.rfc3339Timestamp().as('dateCreated'),
      date_modified: schema.rfc3339Timestamp().as('dateModified'),
    }),
    date_created: schema.rfc3339Timestamp().as('dateCreated'),
    date_modified: schema.rfc3339Timestamp().as('dateModified'),
  },
  { idOptional: true },
);

export const restApiInstrument = schema.modelEntity(
  'instrument',
  {
    instrument_id: schema.id().as('instrumentId'),
    contract_address: schema.reference('contract').as('contractAddress'),
    txid: schema.string().as('txId'),
    freeze_period: schema.rfc3339Timestamp().as('freezePeriod'),
    freeze_txid: schema.string().as('freezeTxId'),
    name: schema.string(),
    transfers_permitted: schema.boolean().as('transfersPermitted'),
    authorized_quantity: schema.number().as('authorizedQuantity'),
    face_value: schema.object(restApiFaceValue).as('faceValue'),
    details: restApiInstrumentProtocolData,
    payload: schema
      .selectTypeViaKey((parent: any) => parent.details?.instrumentType, {
        [INSTRUMENT_TYPE_CURRENCY]: restApiInstrumentCCYProtocolData,
        [INSTRUMENT_TYPE_CREDIT_NOTE]: restApiInstrumentCRNProtocolData,
        [INSTRUMENT_TYPE_COUPON]: restApiInstrumentCOUProtocolData,
        [INSTRUMENT_TYPE_LOYALTY]: restApiInstrumentLOYProtocolData,
        [INSTRUMENT_TYPE_REWARD]: restApiInstrumentRPTProtocolData,
        [INSTRUMENT_TYPE_CASINO]: restApiInstrumentCHPProtocolData,
        [INSTRUMENT_TYPE_TICKET]: restApiInstrumentTICProtocolData,
      })
      .as('instrumentTypeSpecifics'),
    draft: schema.modelEntity('instrumentDraft', {
      id: schema.id(),
      contract_id: schema.id().as('contractId'),
      details: restApiInstrumentProtocolData,
      payload: schema
        .selectTypeViaKey((parent: any) => parent.details?.instrumentType, {
          [INSTRUMENT_TYPE_CURRENCY]: restApiInstrumentCCYProtocolData,
          [INSTRUMENT_TYPE_CREDIT_NOTE]: restApiInstrumentCRNProtocolData,
          [INSTRUMENT_TYPE_COUPON]: restApiInstrumentCOUProtocolData,
          [INSTRUMENT_TYPE_LOYALTY]: restApiInstrumentLOYProtocolData,
          [INSTRUMENT_TYPE_REWARD]: restApiInstrumentRPTProtocolData,
          [INSTRUMENT_TYPE_CASINO]: restApiInstrumentCHPProtocolData,
          [INSTRUMENT_TYPE_TICKET]: restApiInstrumentTICProtocolData,
        })
        .as('instrumentTypeSpecifics'),
      is_posted: schema.boolean().as('isPosted'),
      was_rejected: schema.boolean().as('wasRejected'),
      transfer_fee_locking_script: schema
        .string()
        .as('transferFeeLockingScript'),
      transfer_fee_locking_script_handle: schema
        .string()
        .as('transferFeeLockingScriptHandle'),
      date_created: schema.rfc3339Timestamp().as('dateCreated'),
      date_modified: schema.rfc3339Timestamp().as('dateModified'),
    }),
    date_created: schema.rfc3339Timestamp().as('dateCreated'),
    date_modified: schema.rfc3339Timestamp().as('dateModified'),
  },
  { idOptional: true },
);

export const restApiContractTimelineEvent = schema.object({
  event_type: schema
    .enumerated([
      'contract_form',
      'contract_amend',
      'contract_expire',
      'instrument_create',
      'instrument_modify',
      'instrument_expire',
      'body_form',
      'body_amend',
      'contract_drafted',
      'contract_proposed',
      'contract_canceled',
      'contract_denied',
      'amend_proposed',
      'amend_canceled',
      'amend_denied',
    ])
    .as('eventType'),
  memo: schema.string(),
  action_code: schema.string().as('actionCode'),
  action: schema
    .selectTypeViaKey('actionCode', {
      I2: restApiInstrumentProtocolData,
      C2: restApiContractProtocolData,
      C7: restApiContractBodyProtocolData,
    })
    .as('actionDetails'),
  instrument_id: schema.reference('instrument').as('instrumentId'),
  instrument_payload_code: schema
    .reference('instrumentType')
    .as('instrumentType'),
  instrument_payload: schema
    .selectTypeViaKey('instrumentType', {
      [INSTRUMENT_TYPE_CURRENCY]: restApiInstrumentCCYProtocolData,
      [INSTRUMENT_TYPE_CREDIT_NOTE]: restApiInstrumentCRNProtocolData,
      [INSTRUMENT_TYPE_COUPON]: restApiInstrumentCOUProtocolData,
      [INSTRUMENT_TYPE_LOYALTY]: restApiInstrumentLOYProtocolData,
      [INSTRUMENT_TYPE_REWARD]: restApiInstrumentRPTProtocolData,
      [INSTRUMENT_TYPE_CASINO]: restApiInstrumentCHPProtocolData,
      [INSTRUMENT_TYPE_TICKET]: restApiInstrumentTICProtocolData,
    })
    .as('instrumentTypeSpecifics'),
  txid: schema.string().as('txId'),
  display_handle: schema.string().as('displayHandle'),
  timestamp: schema.rfc3339Timestamp(),
});

/**
 * A message to display to the user about restricted
 * functionality or other service issues
 */
export interface TPermissionRestrictionMessage {
  topic: string;
  code?: string;
  title?: string;
  message?: string;
  category?: string;
  url?: string;
  action?: string;
}
export const restApiPermissionRestrictionMessage =
  schema.object<TPermissionRestrictionMessage>({
    topic: schema.string(),
    code: schema.string(),
    title: schema.string(),
    message: schema.string(),
    category: schema.string(),
    url: schema.string(),
    action: schema.string(),
  });
